import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../api/GlobalContext';
import { Button } from 'reactstrap';
import EditableLabel from '../../components/Misc/EditableLabel';
import ConfirmationModal from '../../components/Misc/ConfirmationModal';
import CompanyNoteSettings from './CompanyNoteSettings';
import { readRailSetting, writeRailSetting } from '../../utils/workspace/railSetting';

const itemStyle = {
  marginBottom: '20px',
};
const labelStyle = {
  marginLeft: '10px',
  marginRight: '10px',
};
const infoInfoStyle = {
  position: 'absolute',
  backgroundColor: '#f4f4f4',
  padding: '10px',
  border: '1px solid #ccc',
  borderRadius: '5px',
  marginTop: '5px',
  fontSize: '14px',
  zIndex: '9999',
};

/**
 * Renders a row item with label, info icon, and radio buttons.
 *
 * @param {jsStateVar} variableItem - The state variable.
 * @param {string} itemLabel - The label for the row item.
 * @param {string} broadKey - The key for the row item (info and setting).
 * @param {Array} choices - The choices for the radio buttons.
 * @returns {JSX.Element} The rendered row item.
 */
const RowItem = ({
  variableItem,
  itemLabel,
  broadKey,
  choices,
  handleInfoClick,
  showInfo,
  infoMessages,
  handleTextChange,
}) => {
  return (
    <div style={itemStyle}>
      <strong>{itemLabel}: </strong>
      <i
        className="fa fa-info-circle"
        aria-hidden="true"
        onClick={() => handleInfoClick(broadKey)}
        style={{ cursor: 'pointer', marginLeft: '5px' }}
      ></i>
      {showInfo[broadKey] && (
        <div style={infoInfoStyle}>{infoMessages[broadKey]}</div>
      )}

      {choices.map((choice) => (
        <label key={choice} style={labelStyle}>
          <input
            type="radio"
            id={choice}
            name={broadKey}
            value={choice}
            checked={variableItem === choice}
            onChange={(event) =>
              handleTextChange(event.target.value, variableItem, broadKey)
            }
          />{' '}
          {choice}
        </label>
      ))}
    </div>
  );
};

const NotePreferences = ({ userDetails }) => {
  const { apiClient } = useContext(GlobalContext);
  const [includeDate, setIncludeDate] = useState('');
  const [includePatientName, setIncludePatientName] = useState('');
  const [includePatientMetaData, setIncludePatientMetaData] = useState('');
  const [commonAbbreviation, setCommonAbbreviation] = useState('');
  const [commonlyMisspelledWords, setCommonlyMisspelledWords] = useState('');
  const [noteFormat, setNoteFormat] = useState('');
  const [pointOfView, setPointOfView] = useState('');
  const [terminology, setTerminology] = useState('');
  const [noteVoice, setNoteVoice] = useState('');
  const [questionsBatching, setQuestionsBatching] = useState('');
  const [modifyExisting, setModifyExisting] = useState('');
  const [scrubExamples, setScrubExamples] = useState('');
  const [globalInstructions, setGlobalInstructions] = useState('');
  const [showAllEMRTemplates, setShowAllEMRTemplates] = useState('');
  const [showComplianceScore, setShowComplianceScore] = useState('');
  const [forceMicDrop, setForceMicDrop] = useState(false);
  const [isWebPTUser, setIsWebPTUser] = useState(false);
  const [empowerNewVersion, setEmpowerNewVersion] = useState(false);
  const [sidePanelMode, setSidePanelMode] = useState('Tab');
  const [pendingSidePanelMode, setPendingSidePanelMode] = useState(null);
  const [complianceRail, setComplianceRail] = useState('Disable');
  const isChromeExtension = window.location.protocol === 'chrome-extension:';
  const isEdgeExtension = isChromeExtension && /\bEdg\//.test(window.navigator.userAgent);
  const isConfluentUser = apiClient.isConfluentUser;
  const [userNoteRules, setUserNoteRules] = useState(apiClient.userNoteRules);

  const [showInfo, setShowInfo] = useState({
    includeDate: false,
    includePatientName: false,
    includePatientMetaData: false,
    noteFormat: false,
    pointOfView: false,
    noteVoice: false,
    terminology: false,
    questionsBatching: false,
    modifyExisting: false,
    scrubExamples: false,
    showAllEMRTemplates: false,
    showComplianceScore: false,
    forceMicDrop: false,
    isWebPTUser: false,
    empowerNewVersion: false,
    sidePanelMode: false,
    complianceRail: false,
  });
  const payloadRef = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!isChromeExtension || !window.chrome?.storage?.sync) return;
    // Same rule as getFreeComplianceStatus in background.js: an explicit choice
    // wins; unset means on for installs stamped at install time, off otherwise.
    readRailSetting((on) => setComplianceRail(on ? 'Enable' : 'Disable'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log('User details:', userDetails);
    setIncludeDate(userDetails?.includeDate ? 'Enable' : 'Disable' || false);
    setIncludePatientName(
      userDetails?.includePatientName ? 'Enable' : 'Disable' || false,
    );
    setIncludePatientMetaData(
      userDetails?.includePatientMetaData ? 'Enable' : 'Disable' || false,
    );
    setShowAllEMRTemplates(
      userDetails?.showAllEMRTemplates ? 'Enable' : 'Disable' || false,
    );
    setCommonAbbreviation(userDetails?.commonAbbrev || '');
    setCommonlyMisspelledWords(userDetails?.misspelledWords || '');
    setNoteFormat(userDetails?.noteFormat || '');
    setPointOfView(userDetails?.pointOfView || '');
    setTerminology(userDetails?.terminology || '');
    setNoteVoice(userDetails?.noteVoice || '');
    setGlobalInstructions(userDetails?.globalInstructions || '');
    setQuestionsBatching(
      userDetails?.isBatchingEnabled ? 'Enable' : 'Disable' || false,
    );
    setModifyExisting(
      userDetails?.modifyExisting ? 'Enable' : 'Disable' || false,
    );
    setScrubExamples(
      userDetails?.scrubExamples ? 'Enable' : 'Disable' || false,
    );
    setShowComplianceScore(
      userDetails?.showComplianceScore ? 'Enable' : 'Disable' || false,
    );
    setForceMicDrop(userDetails?.forceMicDrop ? 'Enable' : 'Disable' || false);
    setIsWebPTUser(userDetails?.isWebPTUser ? 'Enable' : 'Disable' || false);
    setEmpowerNewVersion(
      userDetails?.empowerNewVersion ? 'Enable' : 'Disable' || false,
    );
    if (userDetails?.side_panel_mode) {
      setSidePanelMode(userDetails.side_panel_mode === 'tab' ? 'Tab' : 'Window');
    }
    const noteRulesFlag = apiClient.userNoteRules;
    setUserNoteRules(noteRulesFlag);
  }, [userDetails]);

  useEffect(() => {
    // Cleanup function to be called when unmounting
    return () => {
      console.log('payload:......', payloadRef.current);
      const finalPayload = payloadRef.current;
      if (Object.keys(finalPayload).length > 0) {
        apiClient
          .editUserSettings(finalPayload)
          .then(() => console.log('User settings updated successfully'))
          .catch((error) =>
            console.error('Error updating user settings:', error),
          );
      }
    };
  }, []);

  async function applySidePanelMode(newValue, oldValue) {
    let body = { ...payloadRef.current };

    const modeValue = newValue.toLowerCase();
    const previousPayload = payloadRef.current;
    const sidePanelPayload = {
      ...body,
      note_preferences: {
        ...body.note_preferences,
        side_panel_mode: modeValue,
      },
    };

    setSidePanelMode(newValue);
    setPendingSidePanelMode(null);
    userDetails.side_panel_mode = modeValue;
    payloadRef.current = sidePanelPayload;

    try {
      // Persist the backend source of truth before updating Chrome storage,
      // because the storage update tells background.js to reconfigure/close.
      await apiClient.editUserSettings(sidePanelPayload);
      payloadRef.current = {};

      if (window.chrome?.storage?.sync) {
        window.chrome.storage.sync.set({ sidePanelMode: modeValue });
      }
    } catch (error) {
      console.error('Error updating side panel mode:', error);
      payloadRef.current = previousPayload;
      setSidePanelMode(oldValue);
      userDetails.side_panel_mode = oldValue.toLowerCase();
    }
  }

  async function handleTextChange(newValue, oldValue = '', type) {
    let body = { ...payloadRef.current };
    if (newValue !== oldValue) {
      if (type === 'sidePanelMode') {
        setPendingSidePanelMode({ newValue, oldValue });
        return;
      }
      if (type === 'complianceRail') {
        setComplianceRail(newValue);
        writeRailSetting(newValue === 'Enable');
        return;
      }

      // Check if the new value is different from the old value
      if (type === 'includeDate') {
        setIncludeDate(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          include_date: newValue === 'Enable' ? true : false,
        };
        userDetails.includeDate = newValue === 'Enable' ? true : false;
      }
      if (type === 'includePatientName') {
        setIncludePatientName(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          include_patient_name: newValue === 'Enable' ? true : false,
        };
        userDetails.includePatientName = newValue === 'Enable' ? true : false;
      }

      if (type === 'includePatientMetaData') {
        setIncludePatientMetaData(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          include_patient_metadata: newValue === 'Enable' ? true : false,
        };
        userDetails.includePatientMetaData =
          newValue === 'Enable' ? true : false;
      }

      if (type === 'commonlyMisspelledWords') {
        setCommonlyMisspelledWords(newValue);
        body.misspelled_words = newValue;
        userDetails.misspelledWords = newValue;
      }
      if (type === 'commonAbbreviation') {
        setCommonAbbreviation(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          common_abbrev: newValue,
        };
        userDetails.commonAbbrev = newValue;
      }
      if (type === 'noteFormat') {
        setNoteFormat(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          note_format: newValue,
        };
        userDetails.noteFormat = newValue;
      }
      if (type === 'pointOfView') {
        setPointOfView(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          point_of_view: newValue,
        };
        userDetails.pointOfView = newValue;
      }
      if (type === 'noteVoice') {
        setNoteVoice(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          note_voice: newValue,
        };
        userDetails.noteVoice = newValue;
      }
      if (type === 'terminology') {
        setTerminology(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          terminology: newValue,
        };
        userDetails.terminology = newValue;
      }
      if (type === 'questionsBatching') {
        setQuestionsBatching(newValue);
        body.should_batch = newValue === 'Enable' ? true : false;
        userDetails.isBatchingEnabled = newValue === 'Enable' ? true : false;
      }
      if (type === 'modifyExisting') {
        setModifyExisting(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          modify_existing: newValue === 'Enable' ? true : false,
        };
        userDetails.modifyExisting = newValue === 'Enable' ? true : false;
      }
      if (type === 'scrubExamples') {
        setScrubExamples(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          scrub_examples: newValue === 'Enable' ? true : false,
        };
        userDetails.scrubExamples = newValue === 'Enable' ? true : false;
      }
      if (type === 'globalInstructions') {
        setGlobalInstructions(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          global_instructions: newValue,
        };
        userDetails.globalInstructions = newValue;
      }
      if (type === 'showAllEMRTemplates') {
        setShowAllEMRTemplates(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          show_all_emr_templates: newValue === 'Enable' ? true : false,
        };
        userDetails.showAllEMRTemplates = newValue === 'Enable' ? true : false;
        apiClient.showAllEMRTemplates = userDetails.showAllEMRTemplates;
      }
      if (type === 'showComplianceScore') {
        setShowComplianceScore(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          show_compliance_score: newValue === 'Enable' ? true : false,
        };
        userDetails.showComplianceScore = newValue === 'Enable' ? true : false;
        apiClient.showComplianceScore = userDetails.showComplianceScore;
      }
      if (type === 'forceMicDrop') {
        setForceMicDrop(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          force_mic_drop: newValue === 'Enable',
        };
        userDetails.forceMicDrop = newValue === 'Enable';
        apiClient.forceMicDrop = userDetails.forceMicDrop;
      }
      if (type === 'isWebPTUser') {
        setIsWebPTUser(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          is_webpt_user: newValue === 'Enable' ? true : false,
        };
      }
      userDetails.isWebPTUser = newValue === 'Enable' ? true : false;
      apiClient.isWebPTUser = userDetails.isWebPTUser;
      if (type === 'empowerNewVersion') {
        setEmpowerNewVersion(newValue);
        body.note_preferences = {
          ...body.note_preferences,
          empower_new_version: newValue === 'Enable' ? true : false,
        };
      }
      userDetails.empowerNewVersion = newValue === 'Enable' ? true : false;
      apiClient.empowerNewVersion = userDetails.empowerNewVersion;
      console.log('newPayload:......', body); // Log the updated payload
      payloadRef.current = body; // Update the ref with the new payload
    }
  }

  const infoMessages = {
    includeDate: (
      <>
        <strong>On:</strong> Add today's date at the top of your note so the AI
        knows when the patient's visit happened.
        <br />
        <strong>Off:</strong> Omit the date to keep your note more general and
        timeless.
      </>
    ),
    includePatientName: (
      <>
        <strong>On:</strong> Include the patient's name in the note so the AI
        knows who you're talking about.
        <br />
        <strong>Off:</strong> Keep the patient's name out to keep your note more
        general.
      </>
    ),
    includePatientMetaData: (
      <>
        <strong>On:</strong> Include specific metadata about the patient in the
        note, such as their surgery dates, referring doctor details and relevant
        medical history. This helps provide context and improves the accuracy of
        the AI's responses.
        <br />
        <strong>Off:</strong> Exclude patient metadata from the note to focus
        solely on the content without additional context.
      </>
    ),
    noteFormat: (
      <>
        <strong>Paragraph:</strong> Use a paragraph format to write a continuous
        narrative that flows smoothly.
        <br />
        <strong>Bullet Points:</strong> Use bullet points to succinctly list
        distinct pieces of information.
      </>
    ),
    pointOfView: (
      <>
        <strong>First Person:</strong> Use a first-person narrative to provide a
        personal and subjective account of the patient's session, which can help
        in expressing personal observations and actions directly.
        <br />
        <strong>Third Person:</strong> Use third-person narrative to maintain a
        professional and objective tone in your documentation, which can enhance
        the formal quality of the notes.
      </>
    ),
    noteVoice: (
      <>
        <strong>Formal:</strong> Opt for a formal voice to maintain a
        professional tone that aligns with medical documentation standards,
        suitable for official and legal medical records.
        <br />
        <strong>Casual:</strong> Use a casual voice to create a more relaxed and
        approachable tone, which might be preferable in less formal interactions
        or preliminary notes.
      </>
    ),
    terminology: (
      <>
        <strong>Strict:</strong> Use strict terminology to provide precise and
        specific descriptions that are standard in medical environments,
        ensuring that professional peers understand the details clearly.
        <br />
        <strong>Relaxed:</strong> Opt for simplified terminology to make the
        notes more accessible, especially useful when sharing information with
        patients or non-specialist staff.
      </>
    ),
    questionsBatching: (
      <>
        <strong>Enable:</strong> This feature helps in producing logically
        connected sections, reducing repetition and overlapping content, thus
        making the information more streamlined and efficient.
        <br />
        <strong>Disable:</strong> Disabling provides more detailed response for
        each query, though it may result in some redundancy, such as repeated
        information across different sections.
      </>
    ),
    modifyExisting: (
      <>
        <strong>Enable:</strong> Let the AI update your old notes with new
        information from today’s visit. It'll use the old note like a guide.
        <br />
        <strong>Disable:</strong> Have the AI make a completely new note for
        each visit. This way, each note is just about what happened that day.
      </>
    ),
    scrubExamples: (
      <>
        <strong>Enable:</strong> Have the AI clean up any specific details from
        your example notes. This can be helpful if you see the AI using example
        details in the new notes.
        <br />
        <strong>Disable:</strong> Keep all the details in your example notes.
        Disclaimer: This might potentially bleed example details in your current
        visit's note.
      </>
    ),
    globalInstructions: (
      <>
        <strong>Enable:</strong> Select this option to implement the predefined
        global instructions across all notes. This ensures that every document
        includes certain essential information or follows specific formatting
        rules, making the documentation process smoother and more uniform.
        <br />
        <strong>Disable:</strong> Choose this option to allow each note to be
        tailored individually, without the influence of global guidelines. This
        flexibility is useful when notes need to be customized for different
        contexts or specific requirements of a particular session or
        interaction.
      </>
    ),
    showAllEMRTemplates: (
      <>
        <strong>Enable:</strong> This feature allows you to view all the
        templates available in the EMR system rather than just matching the EMR
        1:1 with the AI.
        <br />
        <strong>Disable:</strong> This feature allows you to view only the
        templates that match the EMR 1:1.
      </>
    ),
    showComplianceScore: (
      <>
        <strong>Enable:</strong> Display the compliance score in your notes to
        ensure adherence to guidelines and standards.
        <br />
        <strong>Disable:</strong> Hide the compliance score from your notes if
        it's not needed.
      </>
    ),
    forceMicDrop: (
      <>
        <strong>Enable:</strong> Mic Drop is a less feature rich version of the
        integration with your EMR. You'll have to manyally transfer each field
        from the AI generated note to your EMR by clicking a button.
        <br />
        <strong>Disable:</strong> The AI will automatically transfer the note to
        your EMR. A much better experience
      </>
    ),
    isWebPTUser: (
      <>
        <strong>Disable:</strong> The new integration with WebPT will update
        your templates to a simpler format so you can fill out more information.
      </>
    ),
    empowerNewVersion: (
      <>
        <strong>Enable:</strong> The new integration with Empower will update
        your templates to a simpler format so you can fill out more information.
        <strong>Disable:</strong> I am happy with my old template and note
        generation.
      </>
    ),
    sidePanelMode: (
      <>
        <strong>Window:</strong> One panel shared across all tabs in the window.
        Switching tabs keeps the same panel open.
        <br />
        <strong>Tab:</strong> Each tab has its own independent panel state
        (legacy behavior).
      </>
    ),
    complianceRail: (
      <>
        <strong>Enable:</strong> Show the Comprehend rail at the edge of your EMR
        page to grade the note you are writing and open the side panel. Reload
        the EMR page after changing this.
        <br />
        <strong>Disable:</strong> No rail on EMR pages.
      </>
    ),
  };

  const handleInfoClick = (type) => {
    setShowInfo((prev) => ({
      ...prev,
      [type]: !prev[type], // Toggle the visibility for the clicked type
    }));
  };

  return (
    <>
      {/* Include Date in Note */}
      <RowItem
        variableItem={includeDate}
        itemLabel="Date in Note"
        broadKey="includeDate"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />

      {/* Include Patient Name in Note */}
      <RowItem
        variableItem={includePatientName}
        itemLabel="Patient Name in Note"
        broadKey="includePatientName"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />

      <RowItem
        variableItem={includePatientMetaData}
        itemLabel="Patient Additional Info in Note"
        broadKey="includePatientMetaData"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />

      {/* <div style={itemStyle}>
        <strong>Format: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('noteFormat')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['noteFormat'] && (
          <div style={infoInfoStyle}>{infoMessages['noteFormat']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Paragraph'}
            name={'format'}
            value={'Paragraph'}
            checked={noteFormat === 'Paragraph'}
            onChange={(event) =>
              handleTextChange(event.target.value, noteFormat, 'noteFormat')
            }
          />{' '}
          Paragraph
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Bullets'}
            name={'format'}
            value={'Bullets'}
            checked={noteFormat === 'Bullets'}
            onChange={(event) =>
              handleTextChange(event.target.value, noteFormat, 'noteFormat')
            }
          />{' '}
          Bullet Points
        </label>
      </div>
      <div style={itemStyle}>
        <strong>Point of View: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('pointOfView')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['pointOfView'] && (
          <div style={infoInfoStyle}>{infoMessages['pointOfView']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'First Person'}
            name={'pov'}
            value={'First Person'}
            checked={pointOfView === 'First Person'}
            onChange={(event) =>
              handleTextChange(event.target.value, pointOfView, 'pointOfView')
            }
          />{' '}
          First Person
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Third Person'}
            name={'pov'}
            value={'Third Person'}
            checked={pointOfView === 'Third Person'}
            onChange={(event) =>
              handleTextChange(event.target.value, pointOfView, 'pointOfView')
            }
          />{' '}
          Third Person
        </label>
      </div>

      <div style={itemStyle}>
        <strong>Voice: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('noteVoice')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['noteVoice'] && (
          <div style={infoInfoStyle}>{infoMessages['noteVoice']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Formal'}
            name={'voice'}
            value={'Formal'}
            checked={noteVoice === 'Formal'}
            onChange={(event) =>
              handleTextChange(event.target.value, noteVoice, 'noteVoice')
            }
          />{' '}
          Formal
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Informal'}
            name={'voice'}
            value={'Informal'}
            checked={noteVoice === 'Informal'}
            onChange={(event) =>
              handleTextChange(event.target.value, noteVoice, 'noteVoice')
            }
          />{' '}
          Informal
        </label>
      </div>

      <div style={itemStyle}>
        <strong>Terminology: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('terminology')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['terminology'] && (
          <div style={infoInfoStyle}>{infoMessages['terminology']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Strict'}
            name={'terminology'}
            value={'Strict'}
            checked={terminology === 'Strict'}
            onChange={(event) =>
              handleTextChange(event.target.value, terminology, 'terminology')
            }
          />{' '}
          Strict
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Relaxed'}
            name={'terminology'}
            value={'Relaxed'}
            checked={terminology === 'Relaxed'}
            onChange={(event) =>
              handleTextChange(event.target.value, terminology, 'terminology')
            }
          />{' '}
          Relaxed
        </label>
      </div>
      <div style={itemStyle}>
        <strong>Minimize Redundancy: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('questionsBatching')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['questionsBatching'] && (
          <div style={infoInfoStyle}>{infoMessages['questionsBatching']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Enable'}
            name={'questionsBatching'}
            value={'Enable'}
            checked={questionsBatching === 'Enable'}
            onChange={(event) =>
              handleTextChange(
                event.target.value,
                questionsBatching,
                'questionsBatching',
              )
            }
          />{' '}
          Enable
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Disable'}
            name={'questionsBatching'}
            value={'Disable'}
            checked={questionsBatching === 'Disable'}
            onChange={(event) =>
              handleTextChange(
                event.target.value,
                questionsBatching,
                'questionsBatching',
              )
            }
          />{' '}
          Disable
        </label>
      </div> */}

      <div style={itemStyle}>
        <strong>Update based on old notes: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('modifyExisting')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['modifyExisting'] && (
          <div style={infoInfoStyle}>{infoMessages['modifyExisting']}</div>
        )}
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Enable'}
            name={'modifyExisting'}
            value={'Enable'}
            checked={modifyExisting === 'Enable'}
            onChange={(event) =>
              handleTextChange(
                event.target.value,
                modifyExisting,
                'modifyExisting',
              )
            }
          />{' '}
          Enable
        </label>
        <label style={labelStyle}>
          <input
            type={'radio'}
            id={'Disable'}
            name={'modifyExisting'}
            value={'Disable'}
            checked={modifyExisting === 'Disable'}
            onChange={(event) =>
              handleTextChange(
                event.target.value,
                modifyExisting,
                'modifyExisting',
              )
            }
          />{' '}
          Disable
        </label>
      </div>
      <RowItem
        variableItem={scrubExamples}
        itemLabel="Example Notes Cleanup"
        broadKey="scrubExamples"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      <RowItem
        variableItem={showAllEMRTemplates}
        itemLabel="Show All EMR Templates"
        broadKey="showAllEMRTemplates"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      <RowItem
        variableItem={showComplianceScore}
        itemLabel="Show Compliance Score"
        broadKey="showComplianceScore"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      <RowItem
        variableItem={forceMicDrop}
        itemLabel="Force Mic Drop"
        broadKey="forceMicDrop"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      {/* Show only Flex in WebPT1 */}
      <RowItem
        variableItem={isWebPTUser}
        itemLabel="Old WebPT 1.0 Integration"
        broadKey="isWebPTUser"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      <RowItem
        variableItem={empowerNewVersion}
        itemLabel="Empower New Version"
        broadKey="empowerNewVersion"
        choices={['Enable', 'Disable']}
        handleInfoClick={handleInfoClick}
        showInfo={showInfo}
        infoMessages={infoMessages}
        handleTextChange={handleTextChange}
      />
      {isChromeExtension && (
        <RowItem
          variableItem={complianceRail}
          itemLabel="Compliance Rail"
          broadKey="complianceRail"
          choices={['Enable', 'Disable']}
          handleInfoClick={handleInfoClick}
          showInfo={showInfo}
          infoMessages={infoMessages}
          handleTextChange={handleTextChange}
        />
      )}
      {isChromeExtension && !isEdgeExtension && (
        <>
          <RowItem
            variableItem={sidePanelMode}
            itemLabel="Side Panel Mode"
            broadKey="sidePanelMode"
            choices={['Window', 'Tab']}
            handleInfoClick={handleInfoClick}
            showInfo={showInfo}
            infoMessages={infoMessages}
            handleTextChange={handleTextChange}
          />
          {/* Changing modes reconfigures Chrome side panel state and closes panels. */}
          <ConfirmationModal
            isOpen={Boolean(pendingSidePanelMode)}
            onClose={() => setPendingSidePanelMode(null)}
            onConfirm={() =>
              applySidePanelMode(
                pendingSidePanelMode.newValue,
                pendingSidePanelMode.oldValue,
              )
            }
            confirmButtonText="Change Mode"
          >
            <div>
              <h3 style={{ marginBottom: '0.75rem' }}>
                Change Side Panel Mode?
              </h3>
              <p style={{ marginBottom: '0.75rem' }}>
                Changing side panel mode requires reopening the extension panel
                so Chrome can apply the new tab/window behavior.
              </p>
              <p style={{ marginBottom: 0 }}>
                Any open Comprehend panels in this browser window will close.
              </p>
            </div>
          </ConfirmationModal>
        </>
      )}

      {!isConfluentUser && userNoteRules && (
        <div
          style={{
            marginTop: '30px',
            borderTop: '1px solid #eee',
            paddingTop: '20px',
          }}
        >
          <CompanyNoteSettings context="user" />
        </div>
      )}

      {/* <div style={itemStyle}>
        <strong>Global Instructions: </strong>
        <i
          className="fa fa-info-circle"
          aria-hidden="true"
          onClick={() => handleInfoClick('globalInstructions')}
          style={{ cursor: 'pointer', marginLeft: '5px' }}
        ></i>
        {showInfo['globalInstructions'] && (
          <div style={infoInfoStyle}>{infoMessages['globalInstructions']}</div>
        )}
        <EditableLabel
          text={globalInstructions}
          onChange={(newValue, oldValue) =>
            handleTextChange(newValue, oldValue, 'globalInstructions')
          }
          className="h3"
          placeholder="Begin each note with a summary of the main points"
        />
      </div>
      <div style={itemStyle}>
        <strong>Common Abbreviation: </strong>
        <EditableLabel
          text={commonAbbreviation}
          onChange={(newValue, oldValue) =>
            handleTextChange(newValue, oldValue, 'commonAbbreviation')
          }
          className="h3"
          placeholder="ROM - Range Of Motion, ACL - Anterior Cruciate Ligament, THR - Total Hip Replacement"
        />
      </div>
      <div style={itemStyle}>
        <strong>Commonly Misspelled Words: </strong>
        <EditableLabel
          text={commonlyMisspelledWords}
          onChange={(newValue, oldValue) =>
            handleTextChange(newValue, oldValue, 'commonlyMisspelledWords')
          }
          className="h3"
          placeholder="Hawkins-kennedy test, Neer's test, Valgus stress test"
        />
      </div> */}
    </>
  );
};

export default NotePreferences;
